import { useEffect, useState } from "react";
import { Flame } from "lucide-react";

// Phase 1 compliance gate: 18+ confirmation + IP-based geo check.
// Blocked countries come from backend `/api/config.geo_block_countries`.
// Age ack persists in localStorage; geo check runs once per session.

const AGE_KEY = "flame.age_confirmed_v1";

export default function ComplianceGate({ blockedCountries = [], children }) {
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [geoChecked, setGeoChecked] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [country, setCountry] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(AGE_KEY) === "1") setAgeConfirmed(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo`);
        const j = await r.json();
        if (cancelled) return;
        setCountry(j.country || null);
        if (j.blocked) setGeoBlocked(true);
      } catch {
        // If geo lookup fails, don't block the user — fail-open is intentional per plan.
      } finally {
        if (!cancelled) setGeoChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [blockedCountries]);

  if (!geoChecked) {
    return (
      <div className="pt-32 text-center text-slate-400">Checking region…</div>
    );
  }

  if (geoBlocked) {
    return (
      <div className="pt-32 flex flex-col items-center text-center px-6" data-testid="geo-block-page">
        <Flame className="text-orange-500 flame-flicker" size={48} />
        <h1 className="font-display text-4xl font-black mt-3">Service unavailable in your region</h1>
        <p className="text-slate-400 mt-3 max-w-md">
          Based on your location ({country}) this product is not available. Access from blocked jurisdictions
          may violate local law. If you believe this is in error, email <a className="text-cyan-400" href="mailto:ops@flamefarm.app">ops@flamefarm.app</a>.
        </p>
        <p className="text-slate-500 text-xs mt-6 max-w-md">
          FLAME is an experimental token with no guaranteed value. This is not a bank, exchange, or regulated financial product.
        </p>
      </div>
    );
  }

  if (!ageConfirmed) {
    return (
      <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100] flex items-center justify-center p-6" data-testid="age-gate-modal">
        <div className="card-tile card-flame max-w-md w-full">
          <div className="flex items-center gap-2">
            <Flame className="text-orange-500 flame-flicker" size={28} />
            <h2 className="font-display text-2xl font-black">Adult content ahead</h2>
          </div>
          <p className="text-slate-300 mt-3 text-sm leading-relaxed">
            FLAME Farm & Arcade is a play-to-earn arcade. Games include risk-of-loss mechanics. By entering you confirm:
          </p>
          <ul className="list-disc text-slate-300 text-sm mt-3 pl-6 space-y-1">
            <li>You are at least <b>18 years old</b></li>
            <li>Entering is legal where you live</li>
            <li>FLAME has <b>no guaranteed monetary value</b></li>
            <li>This is not a bank or exchange</li>
          </ul>
          <div className="mt-5 flex gap-3">
            <button data-testid="age-gate-confirm"
              onClick={() => { localStorage.setItem(AGE_KEY, "1"); setAgeConfirmed(true); }}
              className="btn-tactile btn-flame flex-1 font-display">I'm 18+ — Enter</button>
            <a href="https://www.google.com" className="btn-tactile btn-ghost flex-1 text-center font-display" data-testid="age-gate-leave">Leave</a>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
